import type { AddressObject, Attachment, MessageDetail } from "@mailflare/shared";
import { create } from "zustand";

export type ComposeMode = "new" | "reply" | "reply_all" | "forward";

export interface ComposePrefill {
  readonly to?: readonly AddressObject[];
  readonly cc?: readonly AddressObject[];
  readonly bcc?: readonly AddressObject[];
  readonly subject?: string;
  readonly html?: string;
  readonly inReplyTo?: string;
  readonly references?: readonly string[];
  readonly attachments?: readonly Attachment[];
  readonly draftId?: string;
}

export interface ComposeRequest {
  readonly mode: ComposeMode;
  /** Id of the message being replied to/forwarded. */
  readonly sourceMessageId?: string;
  readonly prefill?: ComposePrefill;
}

export interface ComposeState {
  readonly open: boolean;
  readonly mode: ComposeMode;
  readonly prefill: ComposePrefill | null;
  /** Id of the message being replied to/forwarded, for the reply/forward endpoints. */
  readonly sourceMessageId: string | null;
  openCompose: (request?: ComposeRequest) => void;
  closeCompose: () => void;
}

const EMPTY_PREFILL: ComposePrefill = {};

export const useComposeStore = create<ComposeState>()((set) => ({
  open: false,
  mode: "new",
  prefill: null,
  sourceMessageId: null,
  openCompose: (request) =>
    set({
      open: true,
      mode: request?.mode ?? "new",
      prefill: request?.prefill ?? EMPTY_PREFILL,
      sourceMessageId: request?.sourceMessageId ?? null,
    }),
  closeCompose: () => set({ open: false, prefill: null, sourceMessageId: null }),
}));

/** Builds the prefill for a reply, reply-all, or forward from an existing message. */
export function deriveReplyPrefill(
  message: MessageDetail,
  mode: ComposeMode,
  selfAddress: string,
): ComposePrefill {
  const self = selfAddress.toLowerCase();
  const quoted = `<p><br></p><blockquote><p>On ${message.receivedAt}, ${message.from.address} wrote:</p>${message.body.html ?? `<p>${message.body.text}</p>`}</blockquote>`;
  if (mode === "forward") {
    return {
      subject: `Fwd: ${message.subject}`,
      html: quoted,
      attachments: message.attachments,
      ...(message.messageIdHeader !== null ? { references: [message.messageIdHeader] } : {}),
    };
  }
  const recipients =
    mode === "reply_all"
      ? [
          message.from,
          ...message.to.filter((addr) => addr.address.toLowerCase() !== self),
          ...message.cc.filter((addr) => addr.address.toLowerCase() !== self),
        ]
      : [message.from];
  return {
    to: dedupeAddresses(recipients),
    subject: message.subject.startsWith("Re:") ? message.subject : `Re: ${message.subject}`,
    html: quoted,
    ...(message.messageIdHeader !== null
      ? { inReplyTo: message.messageIdHeader, references: [message.messageIdHeader] }
      : {}),
  };
}

function dedupeAddresses(addresses: readonly AddressObject[]): AddressObject[] {
  const seen = new Set<string>();
  const out: AddressObject[] = [];
  for (const address of addresses) {
    const key = address.address.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(address);
  }
  return out;
}
